import type { Message } from '@copilotkit/shared'
import type { AgentState } from './types'
import type { AudienceConfiguration } from '@/lib/domain'
import type { Operation } from 'fast-json-patch'

export interface MessagesResponse {
    messages: Array<Message>
}

export interface ThreadInfo {
    threadId: string
    createdAt: string
    lastUpdated: string
    state: AgentState
}

export class AguiService {
    private apiUrl: string
    private fetchFn: (
        input: RequestInfo | URL,
        init?: RequestInit
    ) => Promise<Response>

    constructor(
        apiUrl: string,
        fetchFn: (
            input: RequestInfo | URL,
            init?: RequestInit
        ) => Promise<Response> = fetch
    ) {
        this.apiUrl = apiUrl
        this.fetchFn = fetchFn
    }

    public async getThreads(): Promise<ThreadInfo[]> {
        // Non-ok rejects in the fetch wrapper (useAuthedFetch), so a failed list
        // surfaces as React Query `isError` without a check here.
        const response = await this.fetchFn(`${this.apiUrl}/thread/list`)
        return response.json() as Promise<ThreadInfo[]>
    }

    public async deleteThread(threadId: string): Promise<void> {
        await this.fetchFn(`${this.apiUrl}/thread/${threadId}`, {
            method: 'DELETE',
        })
    }

    public async getMessages(threadId: string): Promise<MessagesResponse> {
        // A missing/!ok snapshot is normal (e.g. a brand-new thread), so treat
        // any failure as "no messages" rather than surfacing an error.
        try {
            const response = await this.fetchFn(
                `${this.apiUrl}/message_snapshot/${threadId}`
            )
            return (await response.json()) as MessagesResponse
        } catch {
            return { messages: [] }
        }
    }

    public async getState<T>(threadId: string): Promise<T | undefined> {
        // No snapshot yet (new thread) or a failed fetch → undefined, not an error.
        try {
            const response = await this.fetchFn(
                `${this.apiUrl}/state_snapshot/${threadId}`
            )
            const res = (await response.json()) as { snapshot: T }
            return res.snapshot
        } catch {
            return undefined
        }
    }

    public async patchState(
        threadId: string,
        patches: Operation[]
    ): Promise<void> {
        return this.fetchFn(`${this.apiUrl}/state/${threadId}`, {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(patches),
        }).then((response) => {
            if (!response.ok) {
                throw new Error(
                    `Failed to patch state: ${response.status} ${response.statusText}`
                )
            }
        })
    }

    public async getSessionIdFromThreadId(threadId: string): Promise<unknown> {
        return this.fetchFn(
            `${this.apiUrl}/to_session_id?thread_id=${threadId}`
        ).then((response) => {
            if (!response.ok) {
                throw new Error(
                    `Failed to get session ID: ${response.status} ${response.statusText}`
                )
            }
            return response.json()
        })
    }

    /**
     * Ask the backend to (re)generate this thread's title from its current
     * audience cart and persist it. Returns the new title.
     */
    public async updateThreadTitle(
        threadId: string
    ): Promise<{ thread_title: string }> {
        const response = await this.fetchFn(
            `${this.apiUrl}/thread_title/${threadId}`,
            { method: 'POST' }
        )
        if (!response.ok) {
            throw new Error(
                `Failed to update thread title: ${response.status} ${response.statusText}`
            )
        }
        return response.json() as Promise<{ thread_title: string }>
    }

    /**
     * Add an audience configuration to a thread's cart server-side, mirroring the
     * backend CartManagement tools: the service validates the dataset family,
     * computes a real audience size, and upserts by (dataset_id, segment_id).
     * Returns the stored configuration — which may differ from what was sent (a
     * freshly computed `audience_size`, a generated `segment_id`, etc.).
     */
    public async addConfigurationToCart(
        threadId: string,
        config: AudienceConfiguration
    ): Promise<AudienceConfiguration> {
        const response = await this.fetchFn(
            `${this.apiUrl}/add_to_cart/${threadId}`,
            {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(config),
            }
        )
        if (!response.ok) {
            throw new Error(
                `Failed to add to cart: ${response.status} ${response.statusText}`
            )
        }
        return response.json() as Promise<AudienceConfiguration>
    }
}
